import { useContext, useMemo } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { UserContext } from '@contexts/user-context';
import { Alert } from '@pulse-web-ui/alert';
import { breakpoints, typography } from '@pulse-web-ui/theme';
import {
  PlacementDirection,
  TooltipDirection,
  type TooltipProps,
  TriggerDirection,
} from '@pulse-web-ui/tooltip';
import { observer } from 'mobx-react-lite';

import type { MVPProfile, Profile } from '@entities/agent-contract';

import {
  ColumnContainer,
  LabelValue,
  Section,
  TextLink,
  useIsDesktop,
  useStores,
} from '@shared/index';
import { getSupportMailDataValues } from '@shared/utils';

import { useGetMVPData, useGetProfileSectionHeaderStyles } from '../../hooks';
import type { AgentProfileHeaderProps } from '../../model';
import {
  getMVPCodeDataTranslations,
  getMVPWorkDataTranslations,
} from '../../utils';

interface MVPDataSectionProps {
  profile?: Profile;
  mvpProfile?: MVPProfile;
}

export const MVPDataSection = observer(
  ({ profile, mvpProfile }: MVPDataSectionProps) => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktop(breakpoints.DESKTOP);
    const { getUserName } = useContext(UserContext);
    const {
      MainStore: {
        agentUserStore: { agent },
      },
    } = useStores();
    const username = getUserName();
    const { managerEmail } = mvpProfile || {};
    const { managerName } = profile?.employmentDetails || {};
    const isManagerAssigned = managerName;
    const codeDataLabels = getMVPCodeDataTranslations(t);
    const workDataLabels = getMVPWorkDataTranslations(t);
    const isRenderManagerContacts = !!managerEmail;

    const { codeData, workData, isCodeDataExists, isWorkDataExists } =
      useGetMVPData(profile, mvpProfile);

    const values = useMemo(
      () =>
        getSupportMailDataValues({
          t,
          username,
          profile,
          agent,
          appealPlace: window.location.href,
        }),
      [t, username, profile, agent, window.location.href]
    );

    const titleTooltipProps = useMemo<TooltipProps>(
      () => ({
        tooltipText: t('AGENT_PROFILE:tooltip.personalCode'),
        trigger: TriggerDirection.CLICK,
        direction: TooltipDirection.UP,
        arrow: true,
        styles: { width: '328px', zIndex: 9999 },
        placement: PlacementDirection.BODY,
      }),
      [t]
    );

    const headerProps = useMemo<AgentProfileHeaderProps>(
      () => ({
        fontSize: isDesktop ? typography.medium18 : typography.medium16,
        marginBottom: 16,
      }),
      [isDesktop]
    );

    const codeHeader = useGetProfileSectionHeaderStyles({
      title: t('AGENT_PROFILE:headers.personalCode'),
      isDesktop,
      titleTooltipProps,
      ...headerProps,
    });

    const workPlaceHeader = useGetProfileSectionHeaderStyles({
      title: t('AGENT_PROFILE:headers.workPlace'),
      isDesktop,
      ...headerProps,
    });

    return (
      (isCodeDataExists || isWorkDataExists) && (
        <>
          <ColumnContainer $gap={24}>
            {isCodeDataExists && (
              <Section
                data={codeData}
                dataLabels={codeDataLabels}
                header={codeHeader}
              />
            )}

            {isWorkDataExists && (
              <ColumnContainer $gap={16}>
                <Section
                  data={workData}
                  dataLabels={workDataLabels}
                  header={workPlaceHeader}
                />
                {isRenderManagerContacts && (
                  <LabelValue
                    isWithDesktopStyles
                    label={t('AGENT_PROFILE:labels.managerContacts')}
                    value={
                      <TextLink
                        href={`mailto:${managerEmail}`}
                        style={{
                          width: 'fit-content',
                        }}
                      >
                        {managerEmail}
                      </TextLink>
                    }
                  />
                )}
              </ColumnContainer>
            )}
            {!isManagerAssigned && (
              <Alert
                type='info'
                title={t('AGENT_PROFILE:alert.managerIsNotAssignedTitle')}
                description={
                  <Trans
                    components={{
                      supportLink: <TextLink href={values.mailtoHref} />,
                    }}
                  >
                    {t('AGENT_PROFILE:alert.managerIsNotAssignedDescription', {
                      mailTo: t('COMMON:mailto.body'),
                    })}
                  </Trans>
                }
              />
            )}
          </ColumnContainer>
        </>
      )
    );
  }
);
